import { ActionV2, ResourceTypeV2 } from '../types/authorizations.types';

/**
 * Business actions of the Orders & Transfers module
 * (docs/plans/orders-and-transfers.md §4.1). Each is an ACTION permission
 * `{ resource: { type: ACTION, name }, action: PERFORM }` checked on the
 * server with `requirePermission`. Never rename a value: roles store the
 * permission id, and the name is what policies and the UI refer to.
 */
export const ORDERS_MODULE = 'orders_transfers';

export const ORDERS_ACTION_PREFIX = 'orders:';

export const ORDERS_ACTIONS = {
  ORDER_CREATE: 'orders:order.create',
  ORDER_EDIT_ANY: 'orders:order.edit_any',
  ORDER_PLAN: 'orders:order.plan',
  ORDER_CLOSE: 'orders:order.close',
  TRANSFER_DISPATCH: 'orders:transfer.dispatch',
  TRANSFER_RECEIVE: 'orders:transfer.receive',
  TRANSFER_RETURN: 'orders:transfer.return',
  TRANSFER_RECONCILE: 'orders:transfer.reconcile',
  TRANSFER_REDIRECT: 'orders:transfer.redirect',
  PRICE_APPROVE: 'orders:price.approve',
  PURCHASE_QUOTE: 'orders:purchase.quote',
  PURCHASE_CONFIRM: 'orders:purchase.confirm',
  PURCHASE_RECEIVE: 'orders:purchase.receive',
  PURCHASE_REVIEW: 'orders:purchase.review',
  PURCHASE_REPORT: 'orders:purchase.report',
  PURCHASE_APPROVE: 'orders:purchase.approve',
  PAYMENT_PAY: 'orders:payment.pay',
  PAYMENT_AUTHORIZE: 'orders:payment.authorize',
  SUPPLIER_MANAGE: 'orders:supplier.manage',
  STOCK_COUNT: 'orders:stock.count',
  STOCK_RESERVE: 'orders:stock.reserve',
  STOCK_RELEASE_ANY: 'orders:stock.release_any',
  COSTS_VIEW: 'orders:costs.view',
  TASKS_VIEW_ALL: 'orders:tasks.view_all',
  SETTINGS_MANAGE: 'orders:settings.manage',
} as const;

export type OrdersAction = (typeof ORDERS_ACTIONS)[keyof typeof ORDERS_ACTIONS];

export type OrdersActionGroup = 'orders' | 'transfers' | 'purchases' | 'money';

export interface OrdersActionDefinition {
  name: OrdersAction;
  group: OrdersActionGroup;
  /** English label (the i18n key in the web apps). */
  label: string;
  /** Always evaluated fresh on the server: never served from a decision cache. */
  sensitive?: boolean;
}

export const ORDERS_ACTION_CATALOG: readonly OrdersActionDefinition[] = [
  {
    name: ORDERS_ACTIONS.ORDER_CREATE,
    group: 'orders',
    label: 'Create stock orders',
  },
  {
    name: ORDERS_ACTIONS.ORDER_EDIT_ANY,
    group: 'orders',
    label: 'Edit any stock order',
  },
  {
    name: ORDERS_ACTIONS.ORDER_PLAN,
    group: 'orders',
    label: 'Plan how orders are supplied',
  },
  {
    name: ORDERS_ACTIONS.ORDER_CLOSE,
    group: 'orders',
    label: 'Close the remaining balance of an order',
  },
  {
    name: ORDERS_ACTIONS.TRANSFER_DISPATCH,
    group: 'transfers',
    label: 'Prepare and dispatch transfers',
    sensitive: true,
  },
  {
    name: ORDERS_ACTIONS.TRANSFER_RECEIVE,
    group: 'transfers',
    label: 'Receive transfers',
  },
  {
    name: ORDERS_ACTIONS.TRANSFER_RETURN,
    group: 'transfers',
    label: 'Check returned goods',
  },
  {
    name: ORDERS_ACTIONS.TRANSFER_RECONCILE,
    group: 'transfers',
    label: 'Settle transfer differences',
    sensitive: true,
  },
  {
    name: ORDERS_ACTIONS.TRANSFER_REDIRECT,
    group: 'transfers',
    label: 'Redirect goods to another location',
  },
  {
    name: ORDERS_ACTIONS.PRICE_APPROVE,
    group: 'transfers',
    label: 'Approve selling prices',
    sensitive: true,
  },
  {
    name: ORDERS_ACTIONS.PURCHASE_QUOTE,
    group: 'purchases',
    label: 'Create supplier quotations',
  },
  {
    name: ORDERS_ACTIONS.PURCHASE_CONFIRM,
    group: 'purchases',
    label: 'Confirm supplier purchases',
  },
  {
    name: ORDERS_ACTIONS.PURCHASE_RECEIVE,
    group: 'purchases',
    label: 'Receive supplier deliveries',
  },
  {
    name: ORDERS_ACTIONS.PURCHASE_REVIEW,
    group: 'purchases',
    label: 'Review deliveries with differences',
    sensitive: true,
  },
  {
    name: ORDERS_ACTIONS.PURCHASE_REPORT,
    group: 'purchases',
    label: 'Report a purchase made by staff',
  },
  {
    name: ORDERS_ACTIONS.PURCHASE_APPROVE,
    group: 'purchases',
    label: 'Approve staff purchases',
    sensitive: true,
  },
  {
    name: ORDERS_ACTIONS.PAYMENT_PAY,
    group: 'money',
    label: 'Pay suppliers',
    sensitive: true,
  },
  {
    name: ORDERS_ACTIONS.PAYMENT_AUTHORIZE,
    group: 'money',
    label: 'Authorize supplier payments',
    sensitive: true,
  },
  {
    name: ORDERS_ACTIONS.SUPPLIER_MANAGE,
    group: 'money',
    label: 'Manage suppliers',
  },
  { name: ORDERS_ACTIONS.STOCK_COUNT, group: 'money', label: 'Count stock' },
  {
    name: ORDERS_ACTIONS.STOCK_RESERVE,
    group: 'transfers',
    label: 'Reserve and release stock by hand',
  },
  {
    // In no role template: only the Merchant role gets it by default.
    name: ORDERS_ACTIONS.STOCK_RELEASE_ANY,
    group: 'transfers',
    label: 'Release any reserved stock',
    sensitive: true,
  },
  {
    name: ORDERS_ACTIONS.COSTS_VIEW,
    group: 'money',
    label: 'See costs and supplier prices',
  },
  {
    name: ORDERS_ACTIONS.TASKS_VIEW_ALL,
    group: 'money',
    label: "See the whole team's tasks",
  },
  {
    // In no role template: only the Merchant role gets it by default.
    name: ORDERS_ACTIONS.SETTINGS_MANAGE,
    group: 'money',
    label: 'Manage Orders & Transfers settings',
    sensitive: true,
  },
];

export const SENSITIVE_ORDERS_ACTIONS: ReadonlySet<string> = new Set(
  ORDERS_ACTION_CATALOG.filter((a) => a.sensitive).map((a) => a.name),
);

/** The permission to create for an action (`createPermissionV2`). */
export const ordersActionPermission = (definition: OrdersActionDefinition) => ({
  resource: { type: ResourceTypeV2.ACTION, name: definition.name },
  action: ActionV2.PERFORM,
  description: `${ORDERS_MODULE}: ${definition.label}`,
});

export const isOrdersAction = (name: string): name is OrdersAction =>
  name.startsWith(ORDERS_ACTION_PREFIX) &&
  ORDERS_ACTION_CATALOG.some((a) => a.name === name);
